// Hooks da API de criação de campanhas (rascunhos, validação, estimativa, publicação).
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  BlueprintPatch, BuilderOptionsResponse, DraftDto, DraftListResponse, EstimateResponse, MetaPostsResponse, ValidateResponse,
} from '@impulso/shared/api/builder'
import { api } from '../../lib/api'

export const draftsKey = (companyId: string) => ['drafts', companyId] as const
export const draftKey = (companyId: string, id: string) => ['drafts', companyId, id] as const

export function useDrafts(companyId: string) {
  return useQuery({ queryKey: draftsKey(companyId), queryFn: () => api.get<DraftListResponse>(`/companies/${companyId}/drafts`) })
}

export function useDraft(companyId: string, id: string | undefined) {
  return useQuery({
    queryKey: draftKey(companyId, id ?? ''),
    queryFn: () => api.get<DraftDto>(`/companies/${companyId}/drafts/${id}`),
    enabled: !!id,
    // acompanha o progresso enquanto publica: rápido nos primeiros 2 min, depois a cada 15 s, e desiste em ~30 min
    refetchInterval: (q) => (q.state.data?.status !== 'publishing' ? false : q.state.dataUpdateCount < 80 ? 1500 : q.state.dataUpdateCount < 200 ? 15_000 : false),
  })
}

export function useBuilderOptions(companyId: string, renew?: string | null) {
  return useQuery({
    queryKey: ['builder-options', companyId, renew ?? null],
    queryFn: () => api.get<BuilderOptionsResponse>(`/companies/${companyId}/builder/options${renew ? `?renovar=${encodeURIComponent(renew)}` : ''}`),
  })
}

export function useMetaPosts(companyId: string) {
  return useQuery({ queryKey: ['meta-posts', companyId], queryFn: () => api.get<MetaPostsResponse>(`/companies/${companyId}/builder/meta-posts`), staleTime: 60_000 })
}

export function useCreateDraft(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (blueprint: BlueprintPatch) => api.post<DraftDto>(`/companies/${companyId}/drafts`, { blueprint }),
    onSuccess: (d) => {
      qc.setQueryData(draftKey(companyId, d.id), d)
      void qc.invalidateQueries({ queryKey: draftsKey(companyId), exact: true })
    },
  })
}

export function useSaveDraft(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, blueprint }: { id: string; blueprint: BlueprintPatch }) => api.patch<DraftDto>(`/companies/${companyId}/drafts/${id}`, { blueprint }),
    onSuccess: (d) => {
      qc.setQueryData(draftKey(companyId, d.id), d)
      void qc.invalidateQueries({ queryKey: draftsKey(companyId), exact: true })
    },
  })
}

export function useDeleteDraft(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.del<void>(`/companies/${companyId}/drafts/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: draftsKey(companyId), exact: true }),
  })
}

export function useValidate(companyId: string, id: string | undefined, enabled: boolean, version: string) {
  return useQuery({
    queryKey: ['drafts', companyId, id, 'validate', version],
    queryFn: () => api.post<ValidateResponse>(`/companies/${companyId}/drafts/${id}/validate`),
    enabled: !!id && enabled,
  })
}

export function useEstimate(companyId: string, id: string | undefined, enabled: boolean, version: string) {
  return useQuery({
    queryKey: ['drafts', companyId, id, 'estimate', version],
    queryFn: () => api.get<EstimateResponse>(`/companies/${companyId}/drafts/${id}/estimate`),
    enabled: !!id && enabled,
  })
}

export function usePublish(companyId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<DraftDto>(`/companies/${companyId}/drafts/${id}/publish`),
    onSuccess: (d) => {
      qc.setQueryData(draftKey(companyId, d.id), d)
      void qc.invalidateQueries({ queryKey: draftsKey(companyId), exact: true })
    },
  })
}
